import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Check, FileText, Loader2, Notebook, Brush } from 'lucide-react'
import { DocEditor } from '../../components/DocEditor'
import { SheetEditor } from '../../components/SheetEditor'
import { WhiteboardEditor } from '../../components/WhiteboardEditor'
import { api, extractApiError } from '../../lib/api'
import { useRedirectOnNotFound } from '../../lib/useRedirectOnNotFound'
import { fullDocument } from '../../lib/types'

const TYPE_META = {
  doc: { label: 'Doc', icon: Notebook },
  spreadsheet: { label: 'Spreadsheet', icon: FileText },
  whiteboard: { label: 'Whiteboard', icon: Brush },
}

export function DocumentPage() {
  const { id: documentId } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const docQuery = useQuery({
    queryKey: ['document', documentId],
    queryFn: async () => {
      const res = await api.get(`/documents/${documentId}`)
      return fullDocument.parse(res.data)
    },
    enabled: Boolean(documentId),
    retry: false,
  })
  const doc = docQuery.data ?? null
  // Don't strand the editor on a dead "Document not found." page when the doc is
  // deleted (or the URL is stale) — bounce to Files & Links with a notice.
  useRedirectOnNotFound(docQuery, () => navigate('/app/files-links'), 'This document is no longer available.')

  if (docQuery.isLoading) {
    return (
      <div className="p-8 text-[color:var(--color-ink-muted)]">Loading document…</div>
    )
  }

  if (!doc) {
    return (
      <div className="p-8">
        <p className="text-[color:var(--color-ink-muted)]">
          {docQuery.isError ? extractApiError(docQuery.error).message : 'Document not found.'}{' '}
          <Link to="/app" className="text-[color:var(--color-accent-dim)] hover:text-[color:var(--color-ink-strong)]">
            Back to Home
          </Link>
        </p>
      </div>
    )
  }

  return <DocumentEditor doc={doc} qc={qc} />
}

function DocumentEditor({ doc, qc }) {
  const meta = TYPE_META[doc.type]
  const [title, setTitle] = useState(doc.title)
  const [titleSaveState, setTitleSaveState] = useState('saved')

  // Reset on document switch.
  useEffect(() => {
    setTitle(doc.title)
    setTitleSaveState('saved')
  }, [doc.id, doc.title])

  const updateMutation = useMutation({
    mutationFn: async (patch) => {
      const res = await api.patch(`/documents/${doc.id}`, patch)
      return res.data
    },
    onMutate: () => {
      setTitleSaveState('saving')
    },
    onSuccess: () => {
      setTitleSaveState('saved')
      // Update the cache in place so other consumers (sidebar lists later)
      // pick up the new title without a refetch.
      void qc.invalidateQueries({ queryKey: ['document', doc.id] })
      void qc.invalidateQueries({ queryKey: ['documents', doc.spaceId] })
    },
    onError: (err) => {
      setTitleSaveState('error')
      toast.error(extractApiError(err).message)
    },
  })

  function commitTitle() {
    const trimmed = title.trim()
    if (!trimmed) {
      setTitle(doc.title)
      return
    }
    if (trimmed === doc.title) return
    updateMutation.mutate({ title: trimmed })
  }

  function saveBody(body) {
    updateMutation.mutate({ body })
  }

  return (
    <div className="p-8 h-full flex flex-col min-h-0">
      <div className="mb-4 flex items-center gap-2 text-sm text-[color:var(--color-ink-muted)] shrink-0">
        <span className="inline-flex items-center gap-1.5">
          <meta.icon size={14} />
          {meta.label}
        </span>
        <span className="ml-auto inline-flex items-center gap-1.5 text-xs">
          {titleSaveState === 'saving' ? (
            <>
              <Loader2 size={12} className="animate-spin" />
              Saving…
            </>
          ) : titleSaveState === 'error' ? (
            <span className="text-[color:var(--color-priority-urgent)]">Couldn't save</span>
          ) : (
            <>
              <Check size={12} />
              Saved
            </>
          )}
        </span>
      </div>

      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={commitTitle}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            ;(e.target).blur()
          }
        }}
        placeholder="Untitled"
        maxLength={200}
        className="w-full bg-transparent border-0 outline-none font-display text-3xl text-[color:var(--color-ink-strong)] placeholder:text-[color:var(--color-ink-subtle)] focus:ring-0 shrink-0"
      />

      <div className="mt-6 flex-1 min-h-0 flex flex-col">
        {doc.type === 'doc' ? (
          <DocEditor docId={doc.id} spaceId={doc.spaceId} initialBody={doc.body} onSave={saveBody} />
        ) : doc.type === 'spreadsheet' ? (
          <SheetEditor docId={doc.id} initialBody={doc.body} onSave={saveBody} />
        ) : (
          <WhiteboardEditor docId={doc.id} initialBody={doc.body} onSave={saveBody} />
        )}
      </div>
    </div>
  )
}
